import { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';
import type { Organization, UpdateOrganizationRequest } from '../../../types/organization';
import { MIN_SEARCH_LENGTH } from '../../../utils/searchConstants';

interface EditOrganizationProps {
  open: boolean;
  organization: Organization | null;
  onClose: () => void;
  onSubmit: (organizationId: number, data: UpdateOrganizationRequest) => Promise<void>;
}

export default function EditOrganization({ open, organization, onClose, onSubmit }: EditOrganizationProps) {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open && organization) {
      setName(organization.name ?? '');
      setError(null);
    }
  }, [open, organization]);

  const handleTransitionEnd = () => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Назва обов’язкова');
      return;
    }

    if (name.trim().length < MIN_SEARCH_LENGTH) {
      setError(`Назва має містити щонайменше ${MIN_SEARCH_LENGTH} символів (потрібно для пошуку)`);
      return;
    }

    if (!organization) return;

    setLoading(true);
    setError(null);

    try {
      await onSubmit(organization.id, { name: name.trim() });
      onClose();
    } catch (err) {
      console.error('Error updating organization:', err);
      setError('Не вдалося оновити організацію. Спробуйте ще раз.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        transition: {
          onEntered: handleTransitionEnd,
        },
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>Редагувати організацію</DialogTitle>
        <DialogContent>
          <TextField
            inputRef={inputRef}
            label="Назва"
            fullWidth
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError(null);
            }}
            error={!!error}
            helperText={error}
            disabled={loading}
            required
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Скасувати
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {loading ? 'Збереження...' : 'Зберегти'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
